import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Company,
  CreateCompanyInput,
  UpdateCompanyInput,
} from '@accounting/shared';
import { api } from '../lib/api';
import { setActiveCompanyId } from '../stores/useCompanyStore';

export const companiesKey = ['companies'] as const;

/**
 * Active companies by default. Pass `true` to also include archived (inactive)
 * companies — used by the company switcher and the companies admin page so
 * archived data stays accessible.
 */
export function useCompanies(includeInactive = false) {
  return useQuery({
    queryKey: includeInactive ? (['companies', 'all'] as const) : companiesKey,
    queryFn: () =>
      api
        .get<{ companies: Company[] }>('/companies', includeInactive ? { includeInactive: 'true' } : undefined)
        .then((r) => r.companies),
  });
}

/** Archive (deactivate) or reactivate a company. */
export function useSetCompanyActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { companyId: string; isActive: boolean }) =>
      api
        .patch<{ company: Company }>(`/companies/${vars.companyId}`, { isActive: vars.isActive })
        .then((r) => r.company),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: companiesKey });
      qc.invalidateQueries({ queryKey: ['command-center'] });
      qc.invalidateQueries({ queryKey: ['report'] });
    },
  });
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCompanyInput) =>
      api.post<{ company: Company }>('/companies', input).then((r) => r.company),
    onSuccess: (company) => {
      qc.invalidateQueries({ queryKey: companiesKey });
      setActiveCompanyId(company.id);
    },
  });
}

export function useUpdateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { companyId: string; input: UpdateCompanyInput }) =>
      api
        .patch<{ company: Company }>(`/companies/${vars.companyId}`, vars.input)
        .then((r) => r.company),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: companiesKey });
    },
  });
}

/**
 * Permanently delete a company and all of its data. The caller must pass the
 * exact company name as `confirm` (the API re-checks it). Irreversible.
 */
export function useDeleteCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { companyId: string; confirm: string }) =>
      api.delete<{ id: string; name: string }>(
        `/companies/${vars.companyId}?confirm=${encodeURIComponent(vars.confirm)}`,
      ),
    onSuccess: () => {
      // companiesKey (['companies']) is a prefix of ['companies','all'], so this
      // refreshes both the active list and the include-archived list.
      qc.invalidateQueries({ queryKey: companiesKey });
      qc.invalidateQueries({ queryKey: ['command-center'] });
      qc.invalidateQueries({ queryKey: ['report'] });
    },
  });
}
